import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Chip,
  Box,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import moment from "moment";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const TAB_ASSIGN = 0;
const TAB_PURCHASED = 1;

const ManageProductsDialog = ({ open, onClose, onSaved, client }) => {
  const {
    getProducts,
    getUserProducts,
    postUserProducts,
    patchUserProduct,
    cancelSubscription,
    uncancelSubscription,
  } = useApi();
  const { stripeAccountId } = useUserInfo();
  const [tab, setTab] = useState(TAB_ASSIGN);
  const [products, setProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [clientPurchasedProducts, setClientPurchasedProducts] = useState([]);
  const [quantityValues, setQuantityValues] = useState({});
  const [subscriptionActionLoading, setSubscriptionActionLoading] =
    useState(null);
  const [activeTab, setActiveTab] = useState("purchased");
  const [confirmState, setConfirmState] = useState(null);

  const loadProductsData = async () => {
    if (!client) return;
    setProductsLoading(true);
    try {
      const [allProducts, clientProducts, clientPurchasedProducts] =
        await Promise.all([
          getProducts(stripeAccountId),
          getUserProducts(client.id, "assigned"),
          getUserProducts(client.id, "purchased"),
        ]);
      setProducts(allProducts);
      setAssignedProducts(clientProducts);
      setClientPurchasedProducts(clientPurchasedProducts);
      const purchasedPriceIds = new Set(
        (clientPurchasedProducts || []).map((p) => p.priceId)
      );
      const assignedPriceIds = (clientProducts || [])
        .filter((p) => !purchasedPriceIds.has(p.priceId))
        .map((p) => p.priceId);
      setSelectedProducts(assignedPriceIds);
    } catch (error) {
      console.error("Failed to load products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (open && client) {
      setTab(TAB_ASSIGN);
      setActiveTab("purchased");
      setQuantityValues({});
      loadProductsData();
    }
  }, [open, client]);

  const handleSave = async () => {
    try {
      if (Object.keys(quantityValues).length > 0) {
        await Promise.all(
          Object.entries(quantityValues).map(([priceId, value]) =>
            handleQuantityChange(priceId, value)
          )
        );
      }
      if (activeTab === "assigned") {
        const items = selectedProducts.map((priceId) => {
          const product = products.find((p) =>
            p.prices.some((price) => price.priceId === priceId)
          );
          return {
            productId: product.productId,
            priceId: priceId,
          };
        });
        await postUserProducts({
          items,
          type: "assigned",
          userId: client.id,
        });
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save. Please try again.");
    }
  };

  const handleQuantityChange = async (priceId, newValue) => {
    try {
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "updateQuantity",
        value: parseInt(newValue),
      });

      setClientPurchasedProducts((prev) =>
        prev.map((product) =>
          product.priceId === priceId
            ? { ...product, quantityLeft: parseInt(newValue) }
            : product
        )
      );

      setQuantityValues((prev) => ({
        ...prev,
        [priceId]: newValue,
      }));
    } catch (error) {
      console.error("Failed to update quantity:", error);
      alert("Failed to update quantity. Please try again.");
    }
  };

  const productRows = useMemo(() => {
    return products.flatMap((product) =>
      product.prices.map((price) => {
        const purchasedProduct = clientPurchasedProducts.find(
          (up) => up.priceId === price.priceId
        );
        const isPurchased = Boolean(purchasedProduct);
        const isAssigned = assignedProducts.some(
          (up) => up.priceId === price.priceId && !isPurchased
        );

        return {
          product,
          price,
          purchasedProduct,
          isPurchased,
          isAssigned,
        };
      })
    );
  }, [products, clientPurchasedProducts, assignedProducts]);

  const visibleRows = useMemo(() => {
    if (activeTab === "purchased") {
      return productRows.filter((row) => row.isPurchased);
    }
    return productRows.filter((row) => !row.isPurchased);
  }, [productRows, activeTab]);

  const canShowCancelButton = (price, purchasedProduct) =>
    (!!price.recurring || !!purchasedProduct?.recurring) &&
    !!purchasedProduct?.subscriptionId;

  const handleCancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await cancelSubscription(subscriptionId, stripeAccountId);
      await patchUserProduct({
        userId: client.id,
        priceId,
        action: "cancel",
      });
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? {
                ...p,
                cancel_at_period_end: updated?.cancel_at_period_end,
                status: "cancelled",
              }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  const handleUncancelSubscription = async (subscriptionId, priceId) => {
    if (!subscriptionId) return;
    setSubscriptionActionLoading(priceId);
    setConfirmState(null);
    try {
      const updated = await uncancelSubscription(
        subscriptionId,
        stripeAccountId
      );
      setClientPurchasedProducts((prev) =>
        prev.map((p) =>
          p.priceId === priceId
            ? { ...p, cancel_at_period_end: updated?.cancel_at_period_end }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to reinstate subscription:", error);
      alert("Failed to reinstate subscription. Please try again.");
    }
    setSubscriptionActionLoading(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Products for {client?.name}</DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab
              value="purchased"
              label={`Purchased (${
                productRows.filter((r) => r.isPurchased).length
              })`}
            />
            <Tab
              value="assigned"
              label={`Assigned (${
                productRows.filter((r) => !r.isPurchased).length
              })`}
            />
          </Tabs>

          {productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : visibleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {activeTab === "purchased"
                ? "No purchased products found for this client."
                : "No assignable products available."}
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    {activeTab === "purchased" ? (
                      <>
                        <TableCell align="center">Billing Date</TableCell>
                        <TableCell align="center">Cancel</TableCell>
                      </>
                    ) : (
                      <TableCell align="center">Assign</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map(
                    ({
                      price,
                      product,
                      purchasedProduct,
                      isPurchased,
                      isAssigned,
                    }) => (
                      <TableRow key={price.priceId}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{price.metadata.description}</TableCell>
                        <TableCell align="right">
                          ${(price.unitAmount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased ? (
                            purchasedProduct?.status === "cancelled" ||
                            purchasedProduct?.cancel_at_period_end ? (
                              <Chip
                                label={
                                  purchasedProduct?.cancel_at_period_end
                                    ? "Cancelling"
                                    : "Cancelled"
                                }
                                color="default"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Purchased"
                                color="success"
                                size="small"
                              />
                            )
                          ) : isAssigned ? (
                            <Chip
                              label="Assigned"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Not Active"
                              color="default"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {isPurchased && !price.recurring && (
                            <TextField
                              type="number"
                              size="small"
                              value={
                                quantityValues[price.priceId] ??
                                purchasedProduct?.quantityLeft ??
                                0
                              }
                              onChange={(e) =>
                                setQuantityValues((prev) => ({
                                  ...prev,
                                  [price.priceId]: e.target.value,
                                }))
                              }
                              inputProps={{ min: 0 }}
                              sx={{ width: "80px" }}
                            />
                          )}
                        </TableCell>

                        {activeTab === "purchased" ? (
                          <>
                            <TableCell align="center">
                              {price.recurring ? (
                                purchasedProduct?.nextBillingDate ||
                                purchasedProduct?.billingDate ? (
                                  <Typography
                                    variant="body2"
                                    whiteSpace="nowrap"
                                  >
                                    {purchasedProduct.cancel_at_period_end
                                      ? `${moment(
                                          purchasedProduct.nextBillingDate ||
                                            purchasedProduct.billingDate
                                        ).format("MMM D, YYYY")}`
                                      : `${moment(
                                          purchasedProduct.nextBillingDate ||
                                            purchasedProduct.billingDate
                                        ).format("MMM D, YYYY")}`}
                                  </Typography>
                                ) : (
                                  <Typography
                                    color="text.secondary"
                                    variant="body2"
                                  >
                                    —
                                  </Typography>
                                )
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {canShowCancelButton(price, purchasedProduct) ? (
                                purchasedProduct.cancel_at_period_end ? (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "reinstate",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Reinstate
                                  </LoadingButton>
                                ) : (
                                  <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() =>
                                      setConfirmState({
                                        type: "cancel",
                                        subscriptionId:
                                          purchasedProduct.subscriptionId,
                                        priceId: price.priceId,
                                      })
                                    }
                                    loading={
                                      subscriptionActionLoading ===
                                      price.priceId
                                    }
                                  >
                                    Cancel
                                  </LoadingButton>
                                )
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell align="center">
                            <Checkbox
                              checked={
                                isAssigned ||
                                selectedProducts.includes(price.priceId)
                              }
                              onChange={() => {
                                setSelectedProducts((prev) =>
                                  prev.includes(price.priceId)
                                    ? prev.filter((id) => id !== price.priceId)
                                    : [...prev, price.priceId]
                                );
                              }}
                              disabled={isPurchased || isAssigned}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!confirmState}
        onClose={() => setConfirmState(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmState?.type === "cancel" && "Cancel Subscription"}
          {confirmState?.type === "reinstate" && "Reinstate Subscription"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmState?.type === "cancel" &&
              "Cancel this subscription at the end of the billing period? The client will retain access until then."}
            {confirmState?.type === "reinstate" &&
              "Reinstate this subscription? It will continue to renew."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (confirmState?.type === "cancel") {
                handleCancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              } else if (confirmState?.type === "reinstate") {
                handleUncancelSubscription(
                  confirmState.subscriptionId,
                  confirmState.priceId
                );
              }
            }}
            variant="contained"
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageProductsDialog;
